import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { getApiUrl } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

// MUI
import { makeStyles, withStyles } from '@mui/styles';

// MUI Components
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';

// MUI Icons
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const styles = theme => ({
  container: {
    flexGrow: 1,
    position: 'relative'
  },
  suggestionsContainerOpen: {
    position: 'absolute',
    left: 0,
    right: 0,
    zIndex: 1
  },
  suggestion: {
    display: 'block'
  },
  suggestionsList: {
    margin: 0,
    padding: 0,
    listStyleType: 'none'
  },
  textField: {
    width: '100%',
  },
  adminActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  },
  addButton: {
    marginBottom: '16px'
  }
});

const useRowStyles = makeStyles({
  root: {
    '& > *': {
      borderBottom: 'unset',
    },
  },
});

function createData(name, description, date_published, query, hash, archive_hash, archive_size, message_count, request_id, visible) {
  return {
    name,
    description,
    date_published,
    request_id,
    visible,
    details: [
      { field: 'Original Request', value: query },
      { field: "Message Count", value: message_count},
      { field: 'Messages Hash', value: hash },
      { field: 'Archive Download', value: archive_hash, link: archive_hash ? getApiUrl("/archives/" + archive_hash + ".zip") : null },
    ],
  };
}

function AdminRow(props) {
  const { row, onToggleVisibility, onEdit, onDelete } = props;
  const [open, setOpen] = React.useState(false);
  const classes = useRowStyles();

  const handleVisibilityChange = async (event) => {
    const newVisibility = event.target.checked;
    await onToggleVisibility(row.request_id, newVisibility);
  };

  const storeLocation = function (event) {
    try {
      const url = event.view.navigation.currentEntry.url;
      const scroll_id = event.target.id;
      window.sessionStorage.setItem('scroll_history_' + url, scroll_id)
    } catch {
    }
  }

  var link_item = row.name;
  if (row.visible === true) {
    link_item = <Link id={"request_" + row.request_id} to={"/request/" + row.request_id} onClick={storeLocation}>{row.name}</Link>
  }

  return (
    <React.Fragment>
      <TableRow className={classes.root}>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {link_item}
        </TableCell>
        <TableCell align="left">{row.description}</TableCell>
        <TableCell align="right">{row.date_published}</TableCell>
        <TableCell align="center">
          <Chip
            label={row.visible ? "Public" : "Private"}
            color={row.visible ? "success" : "default"}
            variant="outlined"
          />
        </TableCell>
        <TableCell align="center">
          <div className={styles.adminActions}>
            <FormControlLabel
              control={
                <Switch
                  checked={row.visible}
                  onChange={handleVisibilityChange}
                  size="small"
                />
              }
              label=""
            />
            <IconButton size="small" onClick={() => onEdit(row)} color="primary">
              <EditIcon />
            </IconButton>
            <IconButton size="small" onClick={() => onDelete(row.request_id)} color="error">
              <DeleteIcon />
            </IconButton>
          </div>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box px={12}>
              <Table size="small" aria-label="details">
                <TableBody>
                  {row.details.map((detailsRow) => (
                    <TableRow key={detailsRow.field}>
                      <TableCell component="th" scope="row">
                        {detailsRow.field}
                      </TableCell>
                        {detailsRow.link && <TableCell><a href={detailsRow.link}>{detailsRow.value}</a></TableCell>}
                        {(!detailsRow.link) && <TableCell>{detailsRow.value}</TableCell>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

AdminRow.propTypes = {
  row: PropTypes.shape({
    description: PropTypes.string.isRequired,
    date_published: PropTypes.string.isRequired,
    details: PropTypes.arrayOf(
      PropTypes.shape({
        field: PropTypes.string.isRequired,
        value: PropTypes.string,
      }),
    ).isRequired,
    name: PropTypes.string.isRequired,
    visible: PropTypes.bool.isRequired,
    request_id: PropTypes.number.isRequired,
  }).isRequired,
  onToggleVisibility: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default function AdminRequestsTable(props) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const { authFetch } = useAuth();
  const classes = styles();

  useEffect(() => {
    const url = window.location.href;
    var scroll_id = window.sessionStorage.getItem('scroll_history_' + url)
    if (scroll_id) {
      var item = document.getElementById(scroll_id)
      if (item) {
        item.scrollIntoView({behavior: "auto", block: "center", inline: "nearest"});
      }
    }
  });

  var buildRows = async () => {
    setLoading(true);
    try {
      const response = await authFetch(getApiUrl('/api/admin/requests'));
      if (!response.ok) {
        setLoading(false);
        return;
      }
      const request_return_value = await response.json();

      var tRows = [];
      for (const element of (request_return_value.data || [])) {
        tRows.push(
            createData(
                element.name,
                element.description,
                (new Date(element.created_at)).toLocaleString('en-US'),
                element.query,
                element.hash,
                element.archive_hash,
                element.size,
                element.message_count,
                element.id,
                element.visible
            )
        );
      }

      setRows(tRows);
    } catch (error) {
      console.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    buildRows();
  }, []);

  // Refresh when refreshTrigger changes
  useEffect(() => {
    if (props.refreshTrigger > 0) {
      buildRows();
    }
  }, [props.refreshTrigger]);

  const handleToggleVisibility = async (requestId, visible) => {
    try {
      const response = await authFetch(getApiUrl(`/api/admin/requests/${requestId}/visibility`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ visible })
      });

      if (response.ok) {
        // Update local state
        setRows(rows.map(row =>
          row.request_id === requestId
            ? { ...row, visible }
            : row
        ));
      } else {
        console.error('Failed to update visibility');
      }
    } catch (error) {
      console.error('Error updating visibility:', error);
    }
  };

  const handleEdit = (row) => {
    // TODO: Implement edit functionality
    console.log('Edit request:', row);
  };

  const handleDelete = async (requestId) => {
    if (window.confirm('Are you sure you want to delete this request?')) {
      try {
        const response = await authFetch(getApiUrl(`/api/admin/requests/${requestId}`), {
          method: 'DELETE'
        });

        if (response.ok) {
          setRows(rows.filter(row => row.request_id !== requestId));
        } else {
          console.error('Failed to delete request');
        }
      } catch (error) {
        console.error('Error deleting request:', error);
      }
    }
  };

  const handleAddNew = () => {
    props.onShowUploadForm && props.onShowUploadForm();
  };

  return (
    <div>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleAddNew}
        className={classes.addButton}
      >
        Add New Request
      </Button>

      <TableContainer component={Paper}>
        <Table aria-label="admin requests table">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Request Name</TableCell>
              <TableCell align="left">Description</TableCell>
              <TableCell align="right">Date Published</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <AdminRow
                key={row.name + "_" + row.request_id}
                row={row}
                onToggleVisibility={handleToggleVisibility}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

export const AdminRequestsTableStyled = withStyles(styles)(AdminRequestsTable);